import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getArticles } from "@/api/blog";
import type { ArticleI } from "@/types/blog";
import { ArticleIcon } from "@/assets/icons";
import { blogGuide } from "@/content/blogGuide";
import cls from "./styles.module.scss";
import { DeferredImage } from "@/ui/DeferredImage/DeferredImage";

const PAGE_SIZE = 2;

type PreviewItem = Pick<
  ArticleI,
  "id" | "title" | "slug" | "preview_image" | "excerpt"
> & { format?: string };

function ArticlePreview({ article }: { article: PreviewItem }) {
  const [imageFailed, setImageFailed] = useState(false);
  const titleId = `home-article-${article.id}`;

  return (
    <li>
      <Link
        to={`/blog/${article.slug}`}
        className={cls.card}
        aria-labelledby={titleId}
        data-home-article-id={article.id}
      >
        <div className={cls.artwork}>
          {article.preview_image && !imageFailed ? (
            <DeferredImage
              src={article.preview_image}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className={cls.noImage} aria-hidden="true">
              <ArticleIcon />
            </span>
          )}
          {article.format && (
            <span className={cls.formatBadge}>{article.format}</span>
          )}
        </div>
        <div className={cls.cardCopy}>
          <h3 id={titleId}>{article.title}</h3>
          {article.excerpt && <p className={cls.excerpt}>{article.excerpt}</p>}
          <span className={cls.readMore} aria-hidden="true">
            {article.format ? "Открыть памятку ↗" : "Читать статью ↗"}
          </span>
        </div>
      </Link>
    </li>
  );
}

export function HomepageBlog() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [nearby, setNearby] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{
    articles: ArticleI[];
    count: number;
    page: number;
  }>({ articles: [], count: 0, page: 0 });
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const pageCount = Math.max(1, Math.ceil((data.count + 1) / PAGE_SIZE));
  const guide: PreviewItem = {
    id: blogGuide.id,
    slug: blogGuide.slug,
    title: t(blogGuide.titleKey),
    excerpt: t(blogGuide.excerptKey),
    preview_image: blogGuide.image,
    format: t(blogGuide.formatKey),
  };
  const items: PreviewItem[] =
    data.page === 0
      ? [guide, ...data.articles].slice(0, PAGE_SIZE)
      : data.articles;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setNearby(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNearby(true);
        observer.disconnect();
      },
      { root: element.closest("[data-site-scroll]"), rootMargin: "400px 0px" }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!nearby) return;
    let cancelled = false;
    const controller = new AbortController();
    setPhase("loading");
    void getArticles({
      limit: page === 0 ? PAGE_SIZE - 1 : PAGE_SIZE,
      offset: Math.max(0, page * PAGE_SIZE - 1),
      signal: controller.signal,
    })
      .then((response) => {
        if (cancelled) return;
        const lastPage = Math.max(
          0,
          Math.ceil((response.count + 1) / PAGE_SIZE) - 1
        );
        // A publication may be removed while the visitor browses the blog.
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setData({
          articles: response.results
            .filter((article) => article.is_published)
            .slice(0, page === 0 ? PAGE_SIZE - 1 : PAGE_SIZE),
          count: response.count,
          page,
        });
        setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [nearby, page, attempt]);

  function turnPage(nextPage: number) {
    if (phase !== "ready") return;
    setPhase("loading");
    setPage(nextPage);
  }

  return (
    <div
      ref={ref}
      className={cls.preview}
      data-home-blog
      data-blog-phase={phase}
    >
      <div className={cls.heading}>
        <h2 id="blog-title">Блог</h2>
        <Link className={cls.allArticles} to="/blog">
          Все статьи <span aria-hidden="true">↗</span>
        </Link>
      </div>
      <div className={cls.content} aria-busy={phase === "loading"}>
        {phase === "error" && (
          <div className={cls.message}>
            <p role="status">
              Не удалось загрузить статьи. Можно попробовать ещё раз или открыть
              блог.
            </p>
            <button
              type="button"
              className={cls.retry}
              onClick={() => {
                setPhase("loading");
                setAttempt((value) => value + 1);
              }}
            >
              Попробовать ещё раз
            </button>
          </div>
        )}
        {items.length > 0 ? (
          <ul
            key={data.page}
            className={cls.cards}
            aria-label="Статьи блога"
            data-loading={phase === "loading"}
          >
            {items.map((article) => (
              <ArticlePreview key={article.id} article={article} />
            ))}
          </ul>
        ) : (
          phase !== "error" && (
            <div className={cls.message}>
              <p role="status">
                {phase === "loading"
                  ? "Загружаем статьи…"
                  : "Пока нет опубликованных статей."}
              </p>
            </div>
          )
        )}
      </div>
      <div className={cls.footer}>
        <nav className={cls.pager} aria-label="Перелистывание статей">
          <button
            type="button"
            className={cls.arrow}
            aria-label="Предыдущие статьи"
            title="Предыдущие статьи"
            disabled={phase !== "ready" || data.page === 0}
            onClick={() => turnPage(data.page - 1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 6-6 6 6 6" />
            </svg>
          </button>
          <span
            className={cls.pageNumber}
            role="status"
            aria-label={`Страница ${data.page + 1} из ${pageCount}`}
          >
            {String(data.page + 1).padStart(2, "0")}{" "}
            <span aria-hidden="true">/</span>{" "}
            {String(pageCount).padStart(2, "0")}
          </span>
          <button
            type="button"
            className={cls.arrow}
            aria-label="Следующие статьи"
            title="Следующие статьи"
            disabled={phase !== "ready" || data.page + 1 >= pageCount}
            onClick={() => turnPage(data.page + 1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );
}
